import React, { useState, useEffect, useCallback } from 'react'
import { useThemeTokens } from '../themes'
import { Button } from '../components/ui'
import { devApi } from '../lib/api-dev'
import { getToken } from '../lib/api'
import type { 
  TimesheetHistoryItemEnhanced, 
  ReportsQuery
} from '../contracts/timesheet'

// Temporary placeholder components - will be created in separate files
const TimesheetFilters: React.FC<{
  filters: ReportsQuery
  onFiltersChange: (filters: ReportsQuery) => void
}> = ({ filters, onFiltersChange }) => {
  const theme = useThemeTokens()

  const handleFilterChange = (key: keyof ReportsQuery, value: string | undefined) => {
    onFiltersChange({
      ...filters,
      [key]: value || undefined
    })
  }

  return (
    <div style={{
      padding: theme.spacing.md,
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.md,
      boxShadow: theme.shadows.sm,
      marginBottom: theme.spacing.lg
    }}>
      <h3 style={{
        fontSize: theme.typography.fontSize.md,
        fontWeight: theme.typography.fontWeight.medium,
        marginBottom: theme.spacing.md,
        color: theme.colors.text
      }}>
        🔍 Filters
      </h3>
      
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: theme.spacing.md
      }}>
        <div>
          <label style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>
            Start Date
          </label>
          <input
            type="date"
            value={filters.startDate || ''}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
            style={{
              width: '100%',
              padding: theme.spacing.sm,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.borderRadius.sm,
              fontSize: theme.typography.fontSize.sm
            }}
          />
        </div>
        
        <div>
          <label style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>
            End Date
          </label>
          <input
            type="date"
            value={filters.endDate || ''}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
            style={{
              width: '100%',
              padding: theme.spacing.sm,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.borderRadius.sm,
              fontSize: theme.typography.fontSize.sm
            }}
          />
        </div>
        
        <div>
          <label style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>
            Status
          </label>
          <select
            value={filters.status || ''}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            style={{
              width: '100%',
              padding: theme.spacing.sm,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.borderRadius.sm,
              fontSize: theme.typography.fontSize.sm
            }}
          >
            <option value="">All Statuses</option>
            <option value="submitted">Submitted</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        
        <div>
          <label style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>
            Employee
          </label>
          <input
            type="text"
            placeholder="Search employee..."
            value={filters.employee || ''}
            onChange={(e) => handleFilterChange('employee', e.target.value)}
            style={{
              width: '100%',
              padding: theme.spacing.sm,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.borderRadius.sm,
              fontSize: theme.typography.fontSize.sm
            }}
          />
        </div>
      </div>
    </div>
  )
}

const TimesheetSummaryStats: React.FC<{
  timesheets: TimesheetHistoryItemEnhanced[]
}> = ({ timesheets }) => {
  const theme = useThemeTokens()

  // Calculate summary statistics
  const totalTimesheets = timesheets.length
  const totalWorkers = timesheets.reduce((sum, t) => sum + t.totalWorkers, 0)
  const totalHours = timesheets.reduce((sum, t) => sum + t.totalHours, 0)
  const overtimeHours = timesheets.reduce((sum, t) => sum + (t.overtimeHours || 0), 0)

  const statusCounts = timesheets.reduce((counts, t) => {
    counts[t.status] = (counts[t.status] || 0) + 1
    return counts
  }, {} as Record<string, number>)

  const statCards = [
    { label: 'Total Timesheets', value: totalTimesheets, icon: '📋' },
    { label: 'Total Workers', value: totalWorkers, icon: '👥' },
    { label: 'Total Hours', value: Math.round(totalHours * 10) / 10, icon: '⏰' },
    { label: 'Overtime Hours', value: Math.round(overtimeHours * 10) / 10, icon: '💼' },
    { label: 'Submitted', value: statusCounts.submitted || 0, icon: '📤' },
    { label: 'Approved', value: statusCounts.approved || 0, icon: '✅' },
    { label: 'Rejected', value: statusCounts.rejected || 0, icon: '❌' }
  ]

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: theme.spacing.md,
      marginBottom: theme.spacing.lg
    }}>
      {statCards.map((stat, index) => (
        <div
          key={index}
          style={{
            padding: theme.spacing.md,
            backgroundColor: theme.colors.surface,
            borderRadius: theme.borderRadius.md,
            boxShadow: theme.shadows.sm,
            textAlign: 'center'
          }}
        >
          <div style={{ fontSize: theme.typography.fontSize.xl, marginBottom: theme.spacing.xs }}>
            {stat.icon}
          </div>
          <div style={{
            fontSize: theme.typography.fontSize.lg,
            fontWeight: theme.typography.fontWeight.bold,
            color: theme.colors.text,
            marginBottom: theme.spacing.xs
          }}>
            {stat.value}
          </div>
          <div style={{
            fontSize: theme.typography.fontSize.sm,
            color: theme.colors.textSecondary
          }}>
            {stat.label}
          </div>
        </div>
      ))}
    </div>
  )
}

const TimesheetsTable: React.FC<{
  timesheets: TimesheetHistoryItemEnhanced[]
  onApprove: (submissionId: string) => void
  onReject: (submissionId: string, reason: string) => void
  onView: (submissionId: string) => void
  loading: boolean
}> = ({ timesheets, onApprove, onReject, onView, loading }) => {
  const theme = useThemeTokens()

  if (loading) {
    return (
      <div style={{
        padding: theme.spacing.xl,
        textAlign: 'center',
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.md
      }}>
        Loading timesheets...
      </div>
    )
  }

  if (timesheets.length === 0) {
    return (
      <div style={{
        padding: theme.spacing.xl,
        textAlign: 'center',
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.md
      }}>
        No timesheets found matching the current filters.
      </div>
    )
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'submitted': return '#f59e0b'
      case 'approved': return '#10b981'
      case 'rejected': return '#ef4444'
      default: return theme.colors.textSecondary
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'submitted': return '📤'
      case 'approved': return '✅'
      case 'rejected': return '❌'
      default: return '📋'
    }
  }

  return (
    <div style={{
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.md,
      boxShadow: theme.shadows.sm,
      overflow: 'hidden'
    }}>
      <div style={{
        padding: theme.spacing.md,
        borderBottom: `1px solid ${theme.colors.border}`
      }}>
                 <h3 style={{
           fontSize: theme.typography.fontSize.md,
           fontWeight: theme.typography.fontWeight.medium,
           margin: 0,
           color: theme.colors.text
         }}>
          📋 Recent Submissions
        </h3>
      </div>
      
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: theme.colors.background }}>
              <th style={{ padding: theme.spacing.sm, textAlign: 'left', fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>Date</th>
              <th style={{ padding: theme.spacing.sm, textAlign: 'left', fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>Submitted By</th>
              <th style={{ padding: theme.spacing.sm, textAlign: 'left', fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>Workers</th>
              <th style={{ padding: theme.spacing.sm, textAlign: 'left', fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>Hours</th>
              <th style={{ padding: theme.spacing.sm, textAlign: 'left', fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>Status</th>
              <th style={{ padding: theme.spacing.sm, textAlign: 'left', fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {timesheets.map((timesheet, index) => (
              <tr key={index} style={{ borderBottom: `1px solid ${theme.colors.border}` }}>
                <td style={{ padding: theme.spacing.sm, fontSize: theme.typography.fontSize.sm }}>
                  {new Date(timesheet.date).toLocaleDateString()}
                </td>
                <td style={{ padding: theme.spacing.sm, fontSize: theme.typography.fontSize.sm }}>
                  {timesheet.submittedBy || 'Unknown'}
                </td>
                <td style={{ padding: theme.spacing.sm, fontSize: theme.typography.fontSize.sm }}>
                  {timesheet.totalWorkers}
                </td>
                <td style={{ padding: theme.spacing.sm, fontSize: theme.typography.fontSize.sm }}>
                  {timesheet.totalHours}
                  {timesheet.overtimeHours && timesheet.overtimeHours > 0 && (
                    <span style={{ color: '#f59e0b', marginLeft: theme.spacing.xs }}>
                      (+{timesheet.overtimeHours} OT)
                    </span>
                  )}
                </td>
                <td style={{ padding: theme.spacing.sm, fontSize: theme.typography.fontSize.sm }}>
                                     <span style={{
                     color: getStatusColor(timesheet.status),
                     fontWeight: theme.typography.fontWeight.medium
                   }}>
                    {getStatusIcon(timesheet.status)} {timesheet.status.charAt(0).toUpperCase() + timesheet.status.slice(1)}
                  </span>
                </td>
                <td style={{ padding: theme.spacing.sm }}>
                  <div style={{ display: 'flex', gap: theme.spacing.xs }}>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => onView(timesheet.submissionId!)}
                    >
                      👁️ View
                    </Button>
                    {timesheet.status === 'submitted' && (
                      <>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => onApprove(timesheet.submissionId!)}
                        >
                          ✅ Approve
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => onReject(timesheet.submissionId!, 'Needs clarification')}
                        >
                          ❌ Reject
                        </Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export const TimesheetReports: React.FC = () => {
  const theme = useThemeTokens()
  const [timesheets, setTimesheets] = useState<TimesheetHistoryItemEnhanced[]>([])
  const [filters, setFilters] = useState<ReportsQuery>({
    startDate: undefined,
    endDate: undefined,
    employee: undefined,
    status: 'submitted',
    limit: 50,
    offset: 0
  })
  const [loading, setLoading] = useState(true)

  const loadTimesheets = useCallback(async () => {
    setLoading(true)
    try {
      const token = getToken()
      if (!token) {
        throw new Error('No auth token found')
      }

      const response = await devApi.getTimesheetReports(token, filters)
      
      setTimesheets(response.timesheets)
    } catch (error) {
      console.error('Failed to load timesheets:', error)
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    loadTimesheets()
  }, [loadTimesheets])

  const approveTimesheet = async (submissionId: string) => {
    try {
      const token = getToken()
      if (!token) {
        throw new Error('No auth token found')
      }

      await devApi.approveTimesheet(token, submissionId, 'approve')
      console.log('Approving timesheet:', submissionId)
      loadTimesheets() // Refresh data
    } catch (error) {
      console.error('Failed to approve timesheet:', error)
    }
  }

  const rejectTimesheet = async (submissionId: string, reason: string) => {
    try {
      const token = getToken()
      if (!token) {
        throw new Error('No auth token found')
      }

      await devApi.approveTimesheet(token, submissionId, 'reject', reason)
      console.log('Rejecting timesheet:', submissionId, 'Reason:', reason)
      loadTimesheets() // Refresh data
    } catch (error) {
      console.error('Failed to reject timesheet:', error)
    }
  }

  const viewTimesheet = (submissionId: string) => {
    // TODO: Navigate to detailed timesheet view
    console.log('Viewing timesheet:', submissionId)
  }

  const exportTimesheets = () => {
    // TODO: Implement export functionality
    console.log('Exporting timesheets with filters:', filters)
  }

  const printTimesheets = () => {
    // TODO: Implement print functionality
    console.log('Printing timesheets with filters:', filters)
  }

  return (
    <div style={{
      padding: theme.spacing.lg,
      backgroundColor: theme.colors.background,
      minHeight: '100vh'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing.lg
      }}>
        <div>
          <h1 style={{
            fontSize: theme.typography.fontSize.xl,
            fontWeight: theme.typography.fontWeight.bold,
            color: theme.colors.text,
            margin: 0,
            marginBottom: theme.spacing.xs
          }}>
            Timesheet Reports
          </h1>
          <p style={{
            fontSize: theme.typography.fontSize.sm,
            color: theme.colors.textSecondary,
            margin: 0
          }}>
            Review and manage submitted timesheets
          </p>
        </div>
        
        <div style={{ display: 'flex', gap: theme.spacing.sm }}>
          <Button onClick={exportTimesheets}>
            📊 Export
          </Button>
          <Button onClick={printTimesheets}>
            🖨️ Print
          </Button>
        </div>
      </div>

      {/* Filters */}
      <TimesheetFilters 
        filters={filters} 
        onFiltersChange={setFilters}
      />

      {/* Summary Stats */}
      <TimesheetSummaryStats timesheets={timesheets} />

      {/* Timesheets Table */}
      <TimesheetsTable 
        timesheets={timesheets}
        onApprove={approveTimesheet}
        onReject={rejectTimesheet}
        onView={viewTimesheet}
        loading={loading}
      />
    </div>
  )
} 